import { computed, Directive, inject, input, linkedSignal } from '@angular/core';
import { equals, matches } from '../../core/utils/his-array.utils';
import { calculateColumnOrder, normalizeColOrder, TableColumnMover } from '../model/table-column-helper';
import { DevTableConfig, TableColumnMeta } from '../model/table-model';
import { HisTableContextDirective } from './his-table-context.directive';

export interface HisProjectedColumn extends TableColumnMeta {
    readonly isFirst: boolean;
    readonly isLast: boolean;
    readonly sourceIndex: number; // index in original baseline columns
}

type ColumnStateConfig = Pick<DevTableConfig, 'hiddenCols' | 'colOrder'>;

function isColumnStateConfigEqual( config1?: ColumnStateConfig, config2?: ColumnStateConfig ): boolean {
    if ( config1 === config2 ) {
        return true;
    }

    if ( !config1
        || !config2
        || !matches( config1.hiddenCols, config2.hiddenCols )
        || !equals( config1.colOrder, config2.colOrder ) ) {
        return false;
    }
    return true;
}

@Directive( {
    selector: 'p-table[hisColumnState]',
    exportAs: 'hisColumnState',
    standalone: true
} )
export class HisColumnStateDirective {
    private readonly context = inject( HisTableContextDirective );

    public readonly originalColumns = input.required<TableColumnMeta[]>( { alias: 'hisColumnState' } );
    private readonly config = this.context.tableConfig;
    
    public readonly projectedColumns = computed( () => {
        const visibleColumns = this.visibleColumns();
        return visibleColumns.map( ( id, orderIdx ) => {
            const { sourceIndex, column } = this.originalStateMap().get( id )!;
            const modifiedColumn = {
                ...column,
                sourceIndex,
                isFirst: orderIdx === 0,
                isLast: orderIdx === visibleColumns.length - 1
            };
            return modifiedColumn as HisProjectedColumn;
        } );
    } );
    
    /** Internal view on the table configuration, restricted to only react to updates concerning this directive. */
    private readonly configInternal = linkedSignal(() => this.config(), { equal: isColumnStateConfigEqual } );
    private readonly originalStateMap = computed( () => new Map( this.originalColumns().map( ( c, i ) => [c.id, { sourceIndex: i, column: { ...c } }] ) ) );
    private readonly originalColumnIds = computed( () => [...this.originalStateMap().keys()] );
    private readonly hiddenColumnIdSet = computed( () => new Set( this.configInternal().hiddenCols ) );
    
    private readonly currentColOrder = computed( () => this.recomputeOrderFromInputs() );
    private readonly visibleColumns = computed( () => this.currentColOrder()
        .filter( id => !this.hiddenColumnIdSet().has( id ) && this.originalStateMap().has( id ) && this.originalStateMap().get( id )!.column.visible !== false )
    );

    private readonly columnMover = new TableColumnMover( this.currentColOrder, this.visibleColumns );

    private recomputeOrderFromInputs() {
        const newOrderConf = this.configInternal().colOrder;
        return calculateColumnOrder( this.originalColumnIds(), newOrderConf );
    }

    public moveLeft( column: HisProjectedColumn ) {
        if ( column.isFirst ) {
            return;
        }
        this.updateOrder( this.columnMover.moveLeft( column.id ) );
    }

    public moveRight( column: HisProjectedColumn ) {
        if ( column.isLast ) {
            return;
        }
        this.updateOrder( this.columnMover.moveRight( column.id ) );
    }

    private updateOrder( newOrder: string[] ) {
        const normalizedOrder = normalizeColOrder( newOrder, this.originalColumnIds() );
        this.context.setTableConfigValue( 'colOrder', normalizedOrder );
    }
}